import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { OrganizationType } from 'src/app/shared/enums/organization-type.enum';
import OrganizationService from 'src/app/shared/services/organization.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { whitespace } from 'src/app/shared/validators/whitespace.validator';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { KeyValuePipe } from '@angular/common';
import { TranslateOrganizationTypeEnum } from '../../../../../shared/pipes/translate-organization-type-enum.pipe';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AdmG4CustomerNumberComponent } from 'src/app/shared/components/adm-g4-customer-number/adm-g4-customer-number.component';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
interface Step1OrganizationForm {
  name: FormControl<string>;
  identification: FormControl<string | null>;
  organizationType: FormControl<OrganizationType>;
  discountType: FormControl<DiscountType>;
  idTagInvitationActive: FormControl<boolean>;
  compactLogoLink: FormControl<string>;
  logoLink: FormControl<string>;
}
@Component({
  selector: 'adm-step1-organization',
  templateUrl: './step1-organization.component.html',
  styleUrls: ['./step1-organization.component.scss'],
  imports: [
    KeyValuePipe,
    TranslateOrganizationTypeEnum,
    CustomDropDownArrowDirective,
    AdmG4CustomerNumberComponent,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatSlideToggleModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
  ],
})
export class Step1OrganizationComponent {
  private organizationService = inject(OrganizationService);
  private loader = inject(TopLoaderService);
  private snackbarService = inject(SnackbarService);

  private errors: { [controlName: string]: { [error: string]: string } };
  public organizationForm: FormGroup<Step1OrganizationForm> = new FormGroup<Step1OrganizationForm>({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, whitespace(), Validators.minLength(1), Validators.maxLength(50)],
    }),
    identification: new FormControl<string | null>(null),
    organizationType: new FormControl<OrganizationType>(OrganizationType.Business, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    discountType: new FormControl<DiscountType>(DiscountType.Percentages, { nonNullable: true, validators: [Validators.required] }),
    idTagInvitationActive: new FormControl<boolean>(false, { nonNullable: true, validators: [Validators.required] }),
    compactLogoLink: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.pattern(/^https?:\/\/.*\.(png|svg)$/i)],
    }),
    logoLink: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.pattern(/^https?:\/\/.*\.(png|svg)$/i)],
    }),
  });
  public organizationType: typeof OrganizationType = OrganizationType;
  public discountTypes: ({ name: string; index: any } | { name: DiscountType; index: DiscountType })[] = [
    { name: 'kr.', index: DiscountType.FixedAmount },
    { name: '%', index: DiscountType.Percentages },
  ];

  refreshStepEvent = output<number>();

  constructor() {
    this.errors = {
      name: {
        minLength: `Navn skal være på mindst 1 karakterer.`,
        maxLength: `Navn må maks være 50 karakterer.`,
        required: `Navn er påkrævet.`,
        empty: `Navn må ikke være tom.`,
      },
      identification: {
        number: `Kundenr. skal være nummerisk.`,
        minLength: `Kundenr. skal være på mindst 1 karakterer.`,
        maxLength: `Kundenr. må maks være 30 karakterer.`,
        required: `Kundenr. er påkrævet.`,
      },
      compactLogoLink: {
        pattern: `URL'en skal pege på en .svg eller .png fil og må maks være 1mb.`,
      },
      logoLink: {
        pattern: `URL'en skal pege på en .svg eller .png fil og må maks være 1mb.`,
      },
    };
  }

  public save() {
    if (this.organizationForm.valid) {
      const name = this.organizationForm.controls['name'].value;
      const identification = this.organizationForm.controls['identification'].value;
      const organizationType = this.organizationForm.controls['organizationType'].value;
      const discountType = this.organizationForm.controls['discountType'].value;
      const compactLogoLink = this.organizationForm.controls['compactLogoLink'].value;
      const logoLink = this.organizationForm.controls['logoLink'].value;
      const idTagInvitationActive = this.organizationForm.controls['idTagInvitationActive'].value;

      this.loader.setLoading(true);
      this.organizationService
        .createOrganization({
          name,
          identification,
          organizationType,
          discountType,
          logoLink,
          compactLogoLink,
          idTagInvitationActive,
        })
        .pipe(
          catchError((httpError: HttpErrorResponse) => {
            if (httpError.status === 400 && httpError.error) {
              const error = httpError.error as ErrorMessage;
              switch (error.code) {
                case 100006:
                case 100007:
                  this.snackbarService.warning(`Organisationen eksisterer allerede.`);
                  break;
                case 100020:
                  this.snackbarService.warning('Logoet skal være i png eller svg');
                  break;
                case 100021:
                  this.snackbarService.warning('Logoet må ikke være større end 1mb');
                  break;
                case 100022:
                  this.snackbarService.warning('Angiv venligst en gyldig url til logoet');
                  break;
              }
            } else {
              this.snackbarService.error('Der opstod en ukendt fejl');
            }
            return of();
          }),
          finalize(() => this.loader.setLoading(false))
        )
        .subscribe(organizationId => {
          if (organizationId) {
            this.snackbarService.success(`Organisationen blev oprettet.`);
            this.refreshStepEvent.emit(organizationId);
          }
        });
    }
  }

  public getControlErrorMessage(controlName: string, error: string) {
    return this.errors[controlName][error];
  }
}
